import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../../context/CompanyContext';
import api from '../../services/api';
import './Dashboard.css';

const formatCurrency = (n) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(n ?? 0);
};

const Dashboard = () => {
  const { user } = useAuth();
  const { companyId } = useCompany();

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/dashboard`);
      return response.data.data;
    },
    enabled: !!companyId
  });

  const kpis = data || {};
  const incomeChange = kpis.incomeChange ?? 0;
  const expensesChange = kpis.expensesChange ?? 0;
  const gatewayFeesTotal = kpis.gatewayFeesTotal ?? 0;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Dashboard</h2>
        <p>Bienvenido, {user?.fullName}</p>
      </div>

      {isLoading && <div className="dashboard-loading">Cargando datos...</div>}
      {error && <div className="dashboard-error">Error al cargar el dashboard</div>}

      {!isLoading && !error && (
      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <h3>Ingresos del Mes</h3>
            <p className="card-value">{formatCurrency(kpis.income)}</p>
            <p className="card-change">
              {incomeChange >= 0 ? '+' : ''}{incomeChange}% vs mes anterior
            </p>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-icon">💸</div>
          <div className="card-content">
            <h3>Egresos del Mes</h3>
            <p className="card-value">{formatCurrency(kpis.expenses)}</p>
            <p className="card-change">
              {expensesChange >= 0 ? '+' : ''}{expensesChange}% vs mes anterior
            </p>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <h3>Utilidad Neta</h3>
            <p className="card-value">{formatCurrency(kpis.profit)}</p>
            {gatewayFeesTotal > 0 && (
              <p className="card-change card-detail">
                Fees pasarela (mes, comisión + IVA): {formatCurrency(gatewayFeesTotal)}
              </p>
            )}
            <p className="card-change">Margen: {kpis.margin ?? 0}% (sobre ingreso bruto del mes)</p>
          </div>
        </div>

        <Link to="/admin/invoices" className="dashboard-card dashboard-card-link">
          <div className="card-icon">🧾</div>
          <div className="card-content">
            <h3>Facturas Pendientes</h3>
            <p className="card-value">{kpis.pendingInvoicesCount ?? 0}</p>
            <p className="card-change">Requieren aprobación</p>
          </div>
        </Link>

        <Link to="/admin/accounting-entries" className="dashboard-card dashboard-card-link">
          <div className="card-icon">📋</div>
          <div className="card-content">
            <h3>Comprobantes Pendientes</h3>
            <p className="card-value">{kpis.pendingEntriesCount ?? 0}</p>
            <p className="card-change">Requieren aprobación</p>
          </div>
        </Link>
      </div>
      )}

      {!isLoading && !error && (
      <div className="dashboard-section">
        <h3>Próximos Pasos</h3>
        <div className="steps-list">
          <div className="step-item">
            <span className="step-number">1</span>
            <div className="step-content">
              <h4>Configurar Plan de Cuentas</h4>
              <p>Importa o crea el Plan Único de Cuentas (PUC) para tu empresa</p>
            </div>
          </div>
          <div className="step-item">
            <span className="step-number">2</span>
            <div className="step-content">
              <h4>Registrar Terceros</h4>
              <p>Agrega clientes, proveedores y empleados al sistema</p>
            </div>
          </div>
          <div className="step-item">
            <span className="step-number">3</span>
            <div className="step-content">
              <h4>Crear Primer Comprobante</h4>
              <p>Comienza a registrar movimientos contables</p>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Dashboard;

